import { PressableProps, StyleSheet, View, ViewStyle, TouchableOpacity, ScrollView, LayoutChangeEvent, Platform } from 'react-native';
import React, { JSX, useCallback, useState } from 'react';
import ContextAction from './ContextAction';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { XStack, Text, SizableText } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import ErrorBoundary from '@utils/ErrorBoundary';
import AntDesign from '@expo/vector-icons/AntDesign';

export type ArrayAction = {
  functionName: string,
  function: Function,
  icon: JSX.Element | string,
  text: string
}

export type ActionArrayTypes = {
  /**
   * @param actionArray - The array of actions to display
   * @default []
   */
  actionArray: Array<ArrayAction>,
  /**
   * @param activeComponent - The active component to display
   * @default ""
   */
  activeComponent: string,
  /**
   * @param forceMinimized - Whether to force the array to be minimized
   * @default false
   */
  forceMinimized?: boolean,
  /**
   * @param iconStyle - The style to apply to the icon
   * @default {}
   */
  iconStyle?: ViewStyle,
  /**
   * @param buttonStyle - The style to apply to the button
   * @default {}
   */
  buttonStyle?: ViewStyle,
  /**
   * @param smallButton - Whether to make the buttons smaller
   * @default false
   */
  smallButton?: boolean,
  /**
   * @param iconSize - The size of the icon
   * @default 18
   */
  iconSize?: number,
  /**
   * @param maxButtonWidth - The maximum width of the button
   * @default 120
   */
  maxButtonWidth?: number,
  /**
   * @param withShadow - Whether to add a shadow to the button
   * @default false
   */
  withShadow?: boolean,
  /**
   * @param conjoined - Whether to connect the buttons together
   * @default false
   */
  conjoined?: boolean,
  /**
   * @param containerProps - The props to apply to the container
   * @default {}
   */
  containerProps?: PressableProps,
  /**
   * @param disableExpand - Whether to disable the expand button
   * @default false
   */
  disableExpand?: boolean,
  /**
   * @param wrap - Whether to wrap the buttons
   * @default true
   */
  wrap?: boolean,
  /**
   * @param defaultIndex - The default index to display
   * @default 0
   */
  defaultIndex?: number,
  /**
   * @param forceRow - Whether to force the buttons to be in a row
   * @default false
   */
  forceRow?: boolean,
  /**
   * @param scaleToWidth - Whether to scale buttons to fit within the available width
   * @default false
   */
  scaleToWidth?: boolean,
  /**
   * @param containerStyle - Custom style to apply to the container
   * @default {}
   */
  containerStyle?: ViewStyle,
  /**
   * @param maxHeight - Maximum height for the container
   * @default undefined
   */
  maxHeight?: number,
  /**
   * @param stretchWidth - Whether to stretch the container to 100% width
   * @default false
   */
  stretchWidth?: boolean
}

const ActionArray = ({ forceRow, scaleToWidth = false, maxButtonWidth = 120, conjoined = false, defaultIndex, wrap = true, withShadow, smallButton, disableExpand, containerProps, iconSize, iconStyle, buttonStyle, forceMinimized, actionArray, activeComponent, containerStyle, maxHeight, stretchWidth = false }: ActionArrayTypes) => {
  const minimized = !forceMinimized && useSelector((state: DefaultRootState) => state.UI.contextMinimized);
  const theme = themeSelector();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const isNative = Platform.OS !== 'web';

  // Theme colors
  const colors = {
    background: theme ? '#ffffff' : '#374151',
    cardBackground: theme ? '#f8fafc' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#3b82f6',
    smartBlue: 'rgb(90,192,234)',
    activeBackground: theme ? '#3b82f6' : '#1e40af',
    inactiveBackground: theme ? '#f1f5f9' : '#4b5563',
  };

  // Callback to handle the function execution when user interacts
  const _handleFunction = useCallback((item: ArrayAction) => {
    item.function(item.functionName);
  }, []);

  // Handle container layout to measure width for scaleToWidth (native only)
  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    if (scaleToWidth && isNative) {
      const { width } = event.nativeEvent.layout;
      setContainerWidth(width);
    }
  }, [scaleToWidth, isNative]);

  // Determine if we should use row layout (forceRow overrides wrap)
  const shouldUseRow = forceRow || !wrap;

  // Calculate max button width when scaleToWidth is enabled (native only)
  const calculatedMaxButtonWidth = (scaleToWidth && isNative && containerWidth)
    ? containerWidth / actionArray.length
    : maxButtonWidth;

  // Calculate icon size based on button constraints
  // Use iconSize prop if provided, otherwise scale based on available space
  // When scaleToWidth is enabled on native, scale icon size proportionally to button width
  const calculatedIconSize = iconSize || (
    (scaleToWidth && isNative && containerWidth && actionArray.length > 0)
      ? Math.max(12, Math.min(24, Math.round((containerWidth / actionArray.length) * 0.2))) // Scale to ~20% of button width, clamped between 12-24
      : (smallButton ? (isNative ? 16 : 18) : 18)
  );

  const generateActions = (item: ArrayAction, index: number) => {
    const isActive = activeComponent === item.functionName;
    const isFirst = index === 0;
    const isLast = index === actionArray.length - 1;

    // Apply conjoined border radius logic (native has more sophisticated handling)
    const borderRadius = conjoined && isNative ? {
      borderTopLeftRadius: isFirst ? 12 : 0,
      borderBottomLeftRadius: isFirst ? 12 : 0,
      borderTopRightRadius: isLast ? 12 : 0,
      borderBottomRightRadius: isLast ? 12 : 0,
    } : { borderRadius: 12 };

    // Apply conjoined margin logic (native only)
    const marginStyle = (conjoined && isNative) ? {
      marginLeft: isFirst ? 0 : -1, // Overlap borders by 1px
      marginRight: isLast ? 0 : -1,
      marginHorizontal: 0,
    } : {
      marginHorizontal: isNative ? 4 : 4,
      marginVertical: isNative ? 0 : 2,
    };

    // Web-specific style adjustments
    const webStyle = !isNative ? {
      maxHeight: !minimized ? 60 : undefined,
    } : {};

    return (
      <Animatable.View
        key={item.functionName}
        animation="fadeInUp"
        delay={index * 50}
        duration={300}
        style={{ 
          flex: isNative
            ? ((scaleToWidth && shouldUseRow) ? 1 : (forceRow ? 0 : 1))
            : 1,
          backgroundColor: isNative ? (isActive ? colors.smartBlue : colors.inactiveBackground) : undefined,
          ...borderRadius,
          minWidth: (scaleToWidth && isNative && shouldUseRow) ? undefined : (smallButton ? 80 : 120),
          maxWidth: isNative
            ? ((wrap && !forceRow && !scaleToWidth) ? '100%' : calculatedMaxButtonWidth)
            : (wrap ? '100%' : undefined),
          width: (scaleToWidth && isNative && shouldUseRow && containerWidth) ? calculatedMaxButtonWidth : undefined,
          // Remove height: '100%' when maxHeight is set to respect the constraint
          height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
          minHeight: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
          ...(maxHeight && isNative ? { maxHeight } : {}),
        }}
      >
        <ErrorBoundary>
          <ContextAction
            defaultColor={colors.inactiveBackground}
            lastItem={actionArray.length === index + 1}
            index={index}
            smallButton={smallButton}
            buttonText={item.text}
            iconColor={isActive ? '#ffffff' : colors.text}
            textColor={isActive ? '#ffffff' : colors.text}
            disableExpand={disableExpand}
            wrap={isNative ? (wrap && !forceRow) : wrap}
            containerProps={{
              ...containerProps,
              style: {
                flex: isNative
                  ? ((scaleToWidth && shouldUseRow) ? 1 : (forceRow ? 0 : 1))
                  : 1,
                minWidth: (scaleToWidth && isNative && shouldUseRow) ? undefined : (smallButton ? 80 : 120),
                maxWidth: isNative
                  ? ((wrap && !forceRow && !scaleToWidth) ? '100%' : calculatedMaxButtonWidth)
                  : (wrap ? '100%' : undefined),
                // Remove height: '100%' when maxHeight is set to respect the constraint
                height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
                minHeight: (maxHeight && isNative) ? undefined : undefined,
                maxHeight: (maxHeight && isNative) ? maxHeight : undefined,
                alignSelf: 'stretch',
              }
            }}
            iconSize={calculatedIconSize}
            customStyle={{
              ...borderRadius,
              borderWidth: 1,
              borderColor: isActive ? colors.smartBlue : colors.border,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: isActive ? 0.2 : 0.1,
              shadowRadius: isActive ? 4 : 2,
              elevation: isActive ? 3 : 1,
              ...marginStyle,
              backgroundColor: isNative
                ? "transparent"
                : (isActive ? colors.smartBlue : colors.inactiveBackground),
              flex: isNative
                ? ((scaleToWidth && shouldUseRow) ? 1 : (forceRow ? 0 : 1))
                : 1,
              alignSelf: 'stretch',
              minWidth: (scaleToWidth && isNative && shouldUseRow) ? undefined : (smallButton ? 80 : 120),
              maxWidth: isNative
                ? ((wrap && !forceRow && !scaleToWidth) ? '100%' : calculatedMaxButtonWidth)
                : (wrap ? '100%' : undefined),
              // Remove height: '100%' when maxHeight is set to respect the constraint
              height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
              minHeight: (maxHeight && isNative) ? undefined : undefined,
              maxHeight: (maxHeight && isNative) ? maxHeight : undefined,
              ...iconStyle,
              ...(isNative ? buttonStyle : {}),
            }}
            selectedColor={colors.smartBlue}
            onPress={() => _handleFunction(item)}
            icon={item.icon}
            activeComponent={activeComponent}
            name={item.functionName}
          />
        </ErrorBoundary>
      </Animatable.View>
    );
  }

  // Container style with platform-specific adjustments
  // When maxHeight is set, constrain height but allow content to fit naturally
  const baseContainerStyle: ViewStyle = {
    borderRadius: 12,
    flex: 1,
    ...(stretchWidth ? {
      width: '100%',
      alignSelf: 'stretch',
    } : {}),
    ...(isNative ? {
      ...(maxHeight ? {
        maxHeight,
        // Don't set height, let content determine height up to maxHeight
        overflow: 'visible' as const, // Allow content to be visible, flex will constrain it
      } : {
        height: '100%' as const,
        overflow: 'visible' as const,
      }),
    } : {
      maxHeight: maxHeight ?? (!minimized ? 60 : undefined),
      overflow: 'visible' as const, // Allow content to fit naturally
    }),
    ...containerStyle, // Allow custom container style to override
  };

  // Wrapper component - XStack for native, View for web
  const WrapperComponent = isNative ? XStack : View;
  // Force no wrap when wrap is false or forceRow is true
  const shouldWrap = wrap && !forceRow && !scaleToWidth;
  const wrapperProps = isNative ? {
    rowGap: conjoined ? 0 : 8,
    columnGap: conjoined ? 0 : 8,
    left: -20,
    flexWrap: (shouldWrap ? "wrap" : "nowrap") as "wrap" | "nowrap",
    justifyContent: "flex-start" as const,
  } : {};

  return (
    <View
      onLayout={isNative ? handleContainerLayout : undefined}
      style={baseContainerStyle}
    >
      {!shouldUseRow ? (
        <WrapperComponent
          {...wrapperProps}
          style={{
            flexDirection: 'row',
            backgroundColor: theme ? '#f0f0f0' : '#333',
            borderRadius: 12,
            padding: 3,
            flex: 1,
            alignSelf: "stretch",
            ...(isNative ? {
              ...(maxHeight ? {
                maxHeight,
                // Let content determine height, flex will constrain to maxHeight
                height: undefined, // Don't force 100% when maxHeight is set
                minHeight: undefined, // Don't force minHeight when maxHeight is set
                overflow: 'visible',
              } : {
                height: '100%',
                minHeight: '100%',
                overflow: 'visible',
              }),
              alignContent: "flex-start",
              alignItems: 'stretch',
            } : {
              flexWrap: shouldWrap ? 'wrap' : 'nowrap',
              ...(maxHeight ? { maxHeight, height: undefined } : {}),
            }),
          }}
        >
          {conjoined
            ? actionArray.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === actionArray.length - 1;
              const isActive = activeComponent === item.functionName;

              return smallButton ? (
                <Animatable.View
                  key={item.functionName}
                  animation="fadeInUp"
                  delay={index * 50}
                  duration={300}
                  style={{ 
                    flex: isNative
                      ? ((scaleToWidth && shouldUseRow) ? 1 : 1)
                      : 1,
                    // Remove minWidth when forceRow && !wrap to prevent overflow
                    minWidth: (forceRow && !wrap) ? undefined : ((scaleToWidth && isNative && shouldUseRow) ? undefined : (isNative ? 60 : 60)),
                    maxWidth: isNative
                      ? ((!shouldUseRow && !scaleToWidth) ? '100%' : (scaleToWidth && containerWidth ? calculatedMaxButtonWidth : undefined))
                      : (wrap ? '100%' : undefined),
                    width: (forceRow && !wrap) ? undefined : ((scaleToWidth && isNative && shouldUseRow && containerWidth) ? calculatedMaxButtonWidth : undefined),
                    // Remove height: '100%' when maxHeight is set to respect the constraint
                    height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
                    minHeight: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
                    ...(maxHeight && isNative ? { maxHeight } : {}),
                    flexShrink: (forceRow && !wrap) ? 1 : 0,
                  }}
                >
                  <TouchableOpacity
                    style={{
                      paddingVertical: isNative ? 4 : 5,
                      paddingHorizontal: isNative ? 8 : 12,
                      ...(conjoined && isNative ? {
                        borderTopLeftRadius: isFirst ? 12 : 0,
                        borderBottomLeftRadius: isFirst ? 12 : 0,
                        borderTopRightRadius: isLast ? 12 : 0,
                        borderBottomRightRadius: isLast ? 12 : 0,
                        marginLeft: isFirst ? 0 : -1,
                        marginRight: isLast ? 0 : -1,
                      } : {
                        borderRadius: 10,
                        ...(isNative ? { marginRight: 8 } : {})
                      }),
                      backgroundColor: isActive
                        ? (isNative ? colors.activeBackground : (theme ? '#fff' : '#555'))
                        : (isNative ? colors.inactiveBackground : 'transparent'),
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: isNative ? 15 : 8,
                      borderWidth: isNative ? 1 : 0,
                      borderColor: isNative
                        ? (isActive ? colors.activeBackground : colors.border)
                        : undefined,
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: isNative ? 2 : 1 },
                      shadowOpacity: isActive ? (isNative ? 0.2 : 0.1) : (isNative ? 0.1 : 0),
                      shadowRadius: isActive ? (isNative ? 4 : 1) : (isNative ? 2 : 1),
                      elevation: isActive ? (isNative ? 3 : 0) : (isNative ? 1 : 0),
                      // Remove height: '100%' when maxHeight is set to respect the constraint
                      height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
                      minHeight: (maxHeight && isNative) ? undefined : (isNative ? undefined : 36),
                      ...(maxHeight && isNative ? { maxHeight } : {}),
                      // Remove minWidth when forceRow && !wrap to prevent overflow
                      minWidth: (forceRow && !wrap) ? undefined : ((scaleToWidth && isNative && shouldUseRow) ? undefined : 80),
                      flex: isNative
                        ? ((scaleToWidth && shouldUseRow) ? 1 : 1)
                        : 1,
                      alignSelf: 'stretch',
                      maxWidth: isNative
                        ? ((!shouldUseRow && !scaleToWidth) ? '100%' : calculatedMaxButtonWidth)
                        : (wrap ? '100%' : undefined),
                      flexShrink: (forceRow && !wrap) ? 1 : 0,
                      ...(iconStyle || {}),
                      ...(isNative ? (buttonStyle || {}) : {}),
                    }}
                    onPress={() => _handleFunction(item)}
                    accessibilityRole="button"
                    accessibilityLabel={`Show ${item.text}`}
                  >
                    {item.icon && (
                      <View style={{
                        width: isNative ? calculatedIconSize : 18,
                        height: isNative ? calculatedIconSize : 18,
                        minWidth: isNative ? calculatedIconSize : 18,
                        minHeight: isNative ? calculatedIconSize : 18,
                        maxWidth: isNative ? calculatedIconSize : 18,
                        maxHeight: isNative ? calculatedIconSize : 18,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                        ...(isNative ? { aspectRatio: 1, marginBottom: 5 } : {}),
                      }}>
                        {typeof item.icon === 'string'
                          ? <AntDesign
                            name={item.icon as any}
                            size={isNative ? calculatedIconSize : 18}
                            color={isActive
                              ? '#ffffff'
                              : (isNative ? colors.text : (theme ? '#000' : '#fff'))
                            }
                          />
                          : item.icon
                        }
                      </View>
                    )}
                    <SizableText
                      color={isActive
                        ? (isNative ? '#ffffff' : (theme ? '#000' : '#fff'))
                        : (isNative ? colors.text : (theme ? '#000' : '#fff'))
                      }
                      size={isNative ? "$3" : undefined}
                      marginRight={isNative ? 8 : undefined}
                      fontWeight={isActive ? (isNative ? '600' : '600') : (isNative ? '500' : '400')}
                      style={!isNative ? { fontSize: 13 } : undefined}
                    >
                      {item.text.split(' ')[0]}
                    </SizableText>
                  </TouchableOpacity>
                </Animatable.View>
              ) : (
                  isNative ? (
                    <Animatable.View
                      key={item.functionName}
                      animation="fadeInDown"
                      delay={index * 50}
                      duration={300}
                      style={{
                        flex: (scaleToWidth && shouldUseRow) ? 1 : 1,
                        minWidth: (scaleToWidth && shouldUseRow) ? undefined : 120,
                        maxWidth: (!shouldUseRow && !scaleToWidth) ? '100%' : (scaleToWidth && containerWidth ? calculatedMaxButtonWidth : undefined),
                        width: (scaleToWidth && shouldUseRow && containerWidth) ? calculatedMaxButtonWidth : undefined,
                        height: '100%',
                        minHeight: '100%',
                      }}
                    >
                      <ContextAction
                        {...item}
                        defaultColor={colors.inactiveBackground}
                        lastItem={isLast}
                        index={index}
                        buttonText={item.text}
                        iconColor={isActive ? '#ffffff' : colors.text}
                        textColor={isActive ? '#ffffff' : colors.text}
                        disableExpand={disableExpand}
                        wrap={!shouldUseRow}
                        containerProps={{
                          ...containerProps,
                          style: {
                            flex: (scaleToWidth && shouldUseRow) ? 1 : 1,
                            minWidth: (scaleToWidth && shouldUseRow) ? undefined : 120,
                            maxWidth: (!shouldUseRow && !scaleToWidth) ? '100%' : calculatedMaxButtonWidth,
                            height: '100%',
                            minHeight: undefined,
                            maxHeight: undefined,
                            alignSelf: 'stretch',
                          }
                        }}
                        iconSize={calculatedIconSize}
                        customStyle={{
                          ...iconStyle,
                          ...(conjoined ? {
                            borderTopLeftRadius: isFirst ? 12 : 0,
                            borderBottomLeftRadius: isFirst ? 12 : 0,
                            borderTopRightRadius: isLast ? 12 : 0,
                            borderBottomRightRadius: isLast ? 12 : 0,
                            marginLeft: isFirst ? 0 : -1,
                            marginRight: isLast ? 0 : -1,
                          } : {
                            borderEndStartRadius: isFirst ? 12 : 0,
                            borderStartStartRadius: 0,
                            borderStartEndRadius: 0,
                            borderEndEndRadius: isLast ? 12 : 0,
                          }),
                          backgroundColor: isActive ? colors.smartBlue : colors.inactiveBackground,
                          borderWidth: 1,
                          borderColor: isActive ? colors.smartBlue : colors.border,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: isActive ? 0.2 : 0.1,
                          shadowRadius: isActive ? 4 : 2,
                          elevation: isActive ? 3 : 1,
                          flex: (scaleToWidth && shouldUseRow) ? 1 : 1,
                          alignSelf: 'stretch',
                          minWidth: (scaleToWidth && shouldUseRow) ? undefined : 120,
                          maxWidth: (!shouldUseRow && !scaleToWidth) ? '100%' : calculatedMaxButtonWidth,
                          height: '100%',
                          minHeight: undefined,
                          maxHeight: undefined,
                          ...buttonStyle,
                        }}
                        selectedColor={colors.smartBlue}
                        onPress={() => _handleFunction(item)}
                        icon={item.icon}
                        activeComponent={activeComponent}
                        name={item.functionName}
                      />
                    </Animatable.View>
                  ) : (
                      <Animatable.View
                        key={item.functionName}
                        animation="fadeInDown"
                        delay={index * 50}
                        duration={300}
                        style={{
                          flex: 1, 
                          minWidth: 120,
                          maxWidth: wrap ? '100%' : undefined
                        }}
                      >
                    <TouchableOpacity
                      onPress={() => _handleFunction(item)}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 5,
                        borderRadius: 10,
                        backgroundColor: isActive ? (theme ? '#fff' : '#555') : 'transparent',
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 1 },
                        shadowOpacity: isActive ? 0.1 : 0,
                        shadowRadius: 1,
                        flex: 1,
                        alignSelf: 'stretch',
                        minWidth: 120,
                        maxWidth: wrap ? '100%' : undefined,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        ...iconStyle,
                      }}
                    >
                      {item.icon && (
                        <View style={{
                          minWidth: 18,
                          height: 18,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}>
                          {typeof item.icon === 'string' ? <AntDesign name={item.icon as any} size={18} color={theme ? '#000' : '#fff'} /> : item.icon}
                        </View>
                      )}
                      <SizableText
                        color={theme ? '#000' : '#fff'}
                        fontWeight={isActive ? '600' : '400'}
                        style={{ fontSize: 13 }}
                      >
                        {item.text}
                      </SizableText>
                    </TouchableOpacity>
                      </Animatable.View>
                    )
              );
            })
            : actionArray.map((item, index) => generateActions(item, index))
          }
        </WrapperComponent>
      ) : (
          <View
            style={{
              flexDirection: 'row',
              backgroundColor: theme ? '#f0f0f0' : '#333',
              borderRadius: 12,
              padding: 3,
              flex: 1,
              alignSelf: "stretch",
              ...(maxHeight ? { maxHeight } : {}),
              // Ensure container doesn't overflow when forceRow && !wrap
              ...(forceRow && !wrap ? { width: '100%', minWidth: 0 } : {}),
            }}
          >
            <ScrollView
              horizontal={!(scaleToWidth && isNative) && !(forceRow && !wrap)}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                flexDirection: 'row',
                alignItems: isNative ? 'stretch' : 'center',
                ...(isNative && scaleToWidth && containerWidth ? { width: containerWidth } : {}),
                ...(isNative && conjoined ? { gap: 0 } : (isNative ? { gap: 8 } : {})),
                // When forceRow and no wrap, use flex to distribute buttons evenly
                ...(forceRow && !wrap ? {
                  flex: 1,
                  width: '100%',
                  // Ensure content doesn't overflow
                  minWidth: 0,
                } : {}),
                ...(isNative && maxHeight ? {
                  minHeight: undefined,
                } : (isNative ? { minHeight: '100%' } : {})),
              }}
              style={{
                flex: 1,
                ...(isNative ? {
                  ...(maxHeight ? {
                    maxHeight,
                  } : {
                    height: '100%',
                  }),
                  overflow: 'visible',
                } : {
                  ...(maxHeight ? { maxHeight } : {}),
                }),
              }}
            >
              {conjoined
                ? actionArray.map((item, index) => {
                  const isFirst = index === 0;
                  const isLast = index === actionArray.length - 1;
                  const isActive = activeComponent === item.functionName;

                return smallButton ? (
                  <Animatable.View
                    key={item.functionName}
                    animation="fadeInUp"
                    delay={index * 50}
                    duration={300}
                    style={{
                      // When forceRow and no wrap, use flex: 1 to distribute evenly
                      flex: (forceRow && !wrap) ? 1 : ((scaleToWidth && isNative && containerWidth) ? 1 : 0),
                      width: (forceRow && !wrap) ? undefined : ((scaleToWidth && isNative && containerWidth) ? calculatedMaxButtonWidth : undefined),
                      // Remove height: '100%' when maxHeight is set to respect the constraint
                      height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
                      ...(maxHeight && isNative ? { maxHeight } : {}),
                      // Remove minWidth when distributing to prevent overflow
                      minWidth: (forceRow && !wrap) ? undefined : undefined,
                      flexShrink: (forceRow && !wrap) ? 1 : 0,
                    }}
                  >
                    <TouchableOpacity
                      style={{
                        paddingVertical: isNative ? 4 : 5,
                        // Reduce padding when distributing to prevent overflow
                        paddingHorizontal: (forceRow && !wrap) ? (isNative ? 8 : 8) : (isNative ? 16 : 12),
                        ...(conjoined && isNative ? {
                          borderTopLeftRadius: isFirst ? 12 : 0,
                          borderBottomLeftRadius: isFirst ? 12 : 0,
                          borderTopRightRadius: isLast ? 12 : 0,
                          borderBottomRightRadius: isLast ? 12 : 0,
                          marginLeft: isFirst ? 0 : -1,
                          marginRight: isLast ? 0 : -1,
                        } : {
                          borderRadius: 10,
                          ...(isNative && !(forceRow && !wrap) ? { marginRight: 8 } : {})
                        }),
                        backgroundColor: isActive
                          ? (isNative ? colors.activeBackground : (theme ? '#fff' : '#555'))
                          : (isNative ? colors.inactiveBackground : 'transparent'),
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: (forceRow && !wrap) ? (isNative ? 4 : 24) : (isNative ? 8 : 24),
                        borderWidth: isNative ? 1 : 0,
                        borderColor: isNative
                          ? (isActive ? colors.activeBackground : colors.border)
                          : undefined,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: isNative ? 2 : 1 },
                        shadowOpacity: isActive ? (isNative ? 0.2 : 0.1) : (isNative ? 0.1 : 0),
                        shadowRadius: isActive ? (isNative ? 4 : 1) : (isNative ? 2 : 1),
                        elevation: isActive ? (isNative ? 3 : 0) : (isNative ? 1 : 0),
                        // Remove height: '100%' when maxHeight is set to respect the constraint
                        height: (maxHeight && isNative) ? undefined : (isNative ? '100%' : undefined),
                        minHeight: (maxHeight && isNative) ? undefined : (isNative ? undefined : 36),
                        ...(maxHeight && isNative ? { maxHeight } : {}),
                        // When forceRow and no wrap, use flex: 1 to distribute evenly
                        flex: (forceRow && !wrap) ? 1 : ((scaleToWidth && isNative && containerWidth) ? 1 : 0),
                        width: (forceRow && !wrap) ? undefined : ((scaleToWidth && isNative && containerWidth) ? calculatedMaxButtonWidth : undefined),
                        maxWidth: (forceRow && !wrap) ? undefined : (isNative
                          ? (scaleToWidth ? calculatedMaxButtonWidth : maxButtonWidth)
                          : undefined),
                        // Remove minWidth when distributing to prevent overflow
                        minWidth: (forceRow && !wrap) ? undefined : undefined,
                        flexShrink: (forceRow && !wrap) ? 1 : 0,
                        ...(iconStyle || {}),
                        ...(isNative ? (buttonStyle || {}) : {}),
                      }}
                      onPress={() => _handleFunction(item)}
                      accessibilityRole="button"
                      accessibilityLabel={`Show ${item.text}`}
                    >
                      {item.icon && (
                        <View style={{
                          width: isNative ? calculatedIconSize : 18,
                          height: isNative ? calculatedIconSize : 18,
                          minWidth: isNative ? calculatedIconSize : 18,
                          minHeight: isNative ? calculatedIconSize : 18,
                          maxWidth: isNative ? calculatedIconSize : 18,
                          maxHeight: isNative ? calculatedIconSize : 18,
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexShrink: 0,
                          ...(isNative ? { aspectRatio: 1 } : {}),
                        }}>
                          {typeof item.icon === 'string'
                            ? <AntDesign
                              name={item.icon as any}
                              size={isNative ? calculatedIconSize : 18}
                              color={isActive
                                ? '#ffffff'
                                : (isNative ? colors.text : (theme ? '#000' : '#fff'))
                              }
                            />
                            : item.icon
                          }
                        </View>
                      )}
                      <SizableText
                        color={isActive
                          ? (isNative ? '#ffffff' : (theme ? '#000' : '#fff'))
                          : (isNative ? colors.text : (theme ? '#000' : '#fff'))
                        }
                        size={isNative ? "$3" : undefined}
                        fontWeight={isActive ? (isNative ? '600' : '600') : (isNative ? '500' : '400')}
                        style={!isNative ? { fontSize: 13 } : undefined}
                      >
                        {item.text.split(' ')[0]}
                      </SizableText>
                    </TouchableOpacity>
                  </Animatable.View>
                ) : (
                    isNative ? (
                      <Animatable.View
                        key={item.functionName}
                        animation="fadeInDown"
                        delay={index * 50}
                        duration={300}
                        style={{
                          // When forceRow and no wrap, use flex: 1 to distribute evenly
                          flex: (forceRow && !wrap) ? 1 : ((scaleToWidth && containerWidth) ? 1 : 0),
                          width: (forceRow && !wrap) ? undefined : ((scaleToWidth && containerWidth) ? calculatedMaxButtonWidth : undefined),
                          // Remove height: '100%' when maxHeight is set to respect the constraint
                          height: (maxHeight && isNative) ? undefined : '100%',
                          minHeight: (maxHeight && isNative) ? undefined : '100%',
                          ...(maxHeight && isNative ? { maxHeight } : {}),
                          // Remove minWidth when distributing to prevent overflow
                          minWidth: (forceRow && !wrap) ? undefined : undefined,
                          flexShrink: (forceRow && !wrap) ? 1 : 0,
                        }}
                      >
                        <ContextAction
                          {...item}
                          defaultColor={colors.inactiveBackground}
                          lastItem={isLast}
                          index={index}
                          buttonText={item.text}
                          iconColor={isActive ? '#ffffff' : colors.text}
                          textColor={isActive ? '#ffffff' : colors.text}
                          disableExpand={disableExpand}
                          wrap={!shouldUseRow}
                          containerProps={{
                            ...containerProps,
                            style: {
                              // When forceRow and no wrap, use flex: 1 to distribute evenly
                              flex: (forceRow && !wrap) ? 1 : ((scaleToWidth && containerWidth) ? 1 : 0),
                              width: (forceRow && !wrap) ? undefined : ((scaleToWidth && containerWidth) ? calculatedMaxButtonWidth : undefined),
                              // Remove height: '100%' when maxHeight is set to respect the constraint
                              height: (maxHeight && isNative) ? undefined : '100%',
                              minHeight: (maxHeight && isNative) ? undefined : undefined,
                              maxHeight: (maxHeight && isNative) ? maxHeight : undefined,
                              // Remove minWidth when distributing to prevent overflow
                              minWidth: (forceRow && !wrap) ? undefined : undefined,
                              flexShrink: (forceRow && !wrap) ? 1 : 0,
                            } as ViewStyle
                          }}
                          iconSize={calculatedIconSize}
                          customStyle={{
                            ...iconStyle,
                            ...(conjoined ? {
                              borderTopLeftRadius: isFirst ? 12 : 0,
                              borderBottomLeftRadius: isFirst ? 12 : 0,
                              borderTopRightRadius: isLast ? 12 : 0,
                              borderBottomRightRadius: isLast ? 12 : 0,
                              marginLeft: isFirst ? 0 : -1,
                              marginRight: isLast ? 0 : -1,
                            } : {
                              borderEndStartRadius: isFirst ? 12 : 0,
                              borderStartStartRadius: 0,
                              borderStartEndRadius: 0,
                              borderEndEndRadius: isLast ? 12 : 0,
                            }),
                            backgroundColor: isActive ? colors.smartBlue : colors.inactiveBackground,
                            borderWidth: 1,
                            borderColor: isActive ? colors.smartBlue : colors.border,
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: isActive ? 0.2 : 0.1,
                            shadowRadius: isActive ? 4 : 2,
                            elevation: isActive ? 3 : 1,
                            // Remove height: '100%' when maxHeight is set to respect the constraint
                            height: (maxHeight && isNative) ? undefined : '100%',
                            ...(maxHeight && isNative ? { maxHeight } : {}),
                            // When forceRow and no wrap, use flex: 1 to distribute evenly
                            flex: (forceRow && !wrap) ? 1 : ((scaleToWidth && containerWidth) ? 1 : 0),
                            width: (forceRow && !wrap) ? undefined : ((scaleToWidth && containerWidth) ? calculatedMaxButtonWidth : undefined),
                            maxWidth: (forceRow && !wrap) ? undefined : (scaleToWidth ? calculatedMaxButtonWidth : maxButtonWidth),
                            // Remove minWidth when distributing to prevent overflow
                            minWidth: (forceRow && !wrap) ? undefined : undefined,
                            flexShrink: (forceRow && !wrap) ? 1 : 0,
                            ...buttonStyle,
                          }}
                          selectedColor={colors.smartBlue}
                          onPress={() => _handleFunction(item)}
                          icon={item.icon}
                          activeComponent={activeComponent}
                          name={item.functionName}
                        />
                      </Animatable.View>
                    ) : (
                        <Animatable.View
                          key={item.functionName}
                          animation="fadeInDown"
                          delay={index * 50}
                          duration={300}
                        >
                      <TouchableOpacity
                        onPress={() => _handleFunction(item)}
                        style={{
                          paddingHorizontal: 12,
                          paddingVertical: 5,
                          borderRadius: 10,
                          backgroundColor: isActive ? (theme ? '#fff' : '#555') : 'transparent',
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 1 },
                          shadowOpacity: isActive ? 0.1 : 0,
                          shadowRadius: 1,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          ...iconStyle,
                        }}
                          >
                            {item.icon && (
                              <View style={{
                                minWidth: 18,
                                height: 18,
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}>
                                {typeof item.icon === 'string' ? <AntDesign name={item.icon as any} size={18} color={theme ? '#000' : '#fff'} /> : item.icon}
                              </View>
                            )}
                            <SizableText
                              color={theme ? '#000' : '#fff'}
                              fontWeight={isActive ? '600' : '400'}
                              style={{ fontSize: 13 }}
                            >
                              {item.text}
                            </SizableText>
                          </TouchableOpacity>
                        </Animatable.View>
                      )
                );
              })
                : actionArray.map((item, index) => generateActions(item, index))
              }
            </ScrollView>
          </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    height: '100%',
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cardBody: {
    padding: 20,
  },
  title: {
    marginBottom: 8,
    fontWeight: '600',
    fontSize: 18,
  },
  statSection: {
    marginTop: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  statInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#3b82f615',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  statLabel: {
    color: '#6b7280',
    fontWeight: '500',
    marginBottom: 8,
    fontSize: 14,
  },
  statValue: {
    fontWeight: '600',
    fontSize: 20,
    marginBottom: 0,
  },
  chartContainer: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
    gap: 8,
  },
  statButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: '#f1f5f9',
    marginRight: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
    minHeight: 36,
  },
  buttonIcon: {
    width: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statButtonActive: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  statButtonText: {
    color: '#374151',
    fontWeight: '500',
    fontSize: 14,
  },
  statButtonTextActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
});

export default ActionArray;
